import { ReactNode } from "react";
import { useCallContact } from "../../packages/apiv2-react-query/client/apiv2Components";
import { ButtonIconTooltipText, ButtonIconTooltipWrapper, ButtonIconWrapper } from "../../styles/type/buttons";
import { AlertType } from "../../state/reducers/setAlert";
import { FormQuestionModuleType, FormQuestionType, useFormQuestions } from "../../state/context/formQuestions.store";
import { ShowFormQuestion } from "../../functions/helpers";
import { Loader } from "../../styles/loading";
import { VoipProvider } from "../../_types/voip";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@radix-ui/react-tooltip";

type Props = {
	entityType: "candidate" | "client" | "clientcontact";
	entityId?: string;
	clientId?: string;
	provider: VoipProvider | null;
	contactFieldToUse?: "telephone" | "mobile";
	setAlert: (alert: AlertType) => void;
	children?: ReactNode;
	isLoading?: boolean;
};

export const VoipButton = ({ entityType, entityId, contactFieldToUse = "telephone", provider, setAlert, isLoading = false, children = <></>, clientId }: Props) => {
	const { formQuestions } = useFormQuestions();
	const { mutateAsync: call, isPending, isError } = useCallContact();

	if (!entityId) return null;

	if (isError) {
		setAlert({
			type: "error",
			message: "An error occured when attempting to initiate call",
		});
	}

	if (!ShowFormQuestion(formQuestions, FormQuestionModuleType.candidate, FormQuestionType.voipIntegration)) return null;

	const handleClick = async (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		const res = await call({
			body: {
				entityType: entityType,
				entityId: entityId,
				clientId: clientId,
				contactFieldToUse: contactFieldToUse,
				provider: provider,
			},
		});
		const redirectUrl = res?.data?.redirectUrl;

		// Devyce uses an https web softphone URL, so open in a new tab to keep Edun open.
		// Dialpad/Cloudcall return custom URI schemes (dialpad://, cloudcall://) that should
		// be triggered via same-tab navigation to avoid extra blank/error tabs.
		if (redirectUrl) {
			if (provider === "devyce") {
				window.open(redirectUrl, "_blank");
				return;
			}
			window.location.href = redirectUrl;
		}
	};

	return isLoading ? (
		<Loader />
	) : (
		<ButtonIconTooltipWrapper>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger>
						<ButtonIconWrapper onClick={isPending ? undefined : handleClick} disabled={!provider || isPending} title={`Click-to-call using ${provider}`}>
							{children}
						</ButtonIconWrapper>
					</TooltipTrigger>
					{!provider && (
						<TooltipContent side="bottom">
							<ButtonIconTooltipText>Please configure a VOIP provider for click-to-call</ButtonIconTooltipText>
						</TooltipContent>
					)}
				</Tooltip>
			</TooltipProvider>
		</ButtonIconTooltipWrapper>
	);
};
